import { radarAnalytics } from './radarAnalytics';
import { beginAuthDiagnostic } from './radarAuthDiagnostics';
import { RADAR_LOCALES } from '@/i18n/radarLanding';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import { useEffect, useRef, useState } from 'react';
import { ArrowRight, LoaderCircle, Mail, ShieldCheck } from 'lucide-react';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { isConnectionError } from '@/lib/connectionErrors';
import { optionalRequestId } from '@/lib/requestId';

export function RadarSignIn({ initialError = '', initialEmail = '', emailOnly = false, context }: {
  initialError?: string; initialEmail?: string; emailOnly?: boolean; context?: 'expo';
}) {
  const { t, e, locale } = useRadarLocale();
  const [email, setEmail] = useState(initialEmail);
  const [message, setMessage] = useState(initialError);
  const [isError, setIsError] = useState(!!initialError);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const pending = useRef(false);
  const flow = useRef({ email: '', id: '' });
  const [diagnosticId, setDiagnosticId] = useState('');
  const observation = useRef<ReturnType<typeof beginAuthDiagnostic> | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; observation.current?.cancelObservation(); };
  }, []);
  const [sentEmail, setSentEmail] = useState('');
  const [code, setCode] = useState('');
  const [retryByEmail, setRetryByEmail] = useState<Record<string, number>>({});
  const targetEmail = sentEmail || email.trim();
  const retryAt = retryByEmail[targetEmail.toLowerCase()] || 0;
  const [now, setNow] = useState(Date.now);
  const secondsRemaining = Math.max(0, Math.ceil((retryAt - now) / 1000));

  useEffect(() => {
    setNow(Date.now());
    if (!retryAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [retryAt]);

  function begin(label: string) {
    if (pending.current) return false;
    pending.current = true;
    setBusy(true);
    setProgress(label);
    setMessage(label);
    setIsError(false);
    return true;
  }

  function finish() { pending.current = false; setBusy(false); setProgress(''); }

  function showError(error: unknown, fallback: string) {
    const expired = (error as { code?: string } | null)?.code === 'otp_expired';
    setMessage(expired ? 'This code is invalid or has expired. Try again or request a new code.'
      : isConnectionError(error) ? 'Connection interrupted. Check your connection and try again. We could not confirm the result.'
      : error instanceof Error ? error.message : fallback);
    setIsError(true);
  }

  function diagnostic(stage: 'send' | 'verify') {
    try {
      const normalizedEmail = targetEmail.trim().toLowerCase();
      if (!flow.current.id || flow.current.email !== normalizedEmail) {
        flow.current = { email: normalizedEmail, id: optionalRequestId() || '' };
      }
      setDiagnosticId(flow.current.id);
      observation.current?.cancelObservation();
      observation.current = beginAuthDiagnostic(normalizedEmail, flow.current.id, stage, () => {
        if (mounted.current) setMessage('This request is taking longer than expected. Its result is not yet known.');
      });
      return observation.current;
    } catch {
      // Missing browser telemetry primitives must never prevent a valid login.
      return { finish() {}, cancelObservation() {} };
    }
  }

  async function signInWithGoogle() {
    if (!begin('Opening Google sign-in…')) return;
    radarAnalytics.capture('radar_auth_started', { auth_method: 'google' });
    try {
      const client = getSupabaseBrowserClient();
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/buyer-radar/app` },
      });
      if (error) throw error;
    } catch (error) {
      radarAnalytics.capture('radar_auth_result', { auth_method: 'google', outcome: 'failed' });
      setMessage(error instanceof Error ? error.message : 'Could not start sign-in.');
      setIsError(true);
      finish();
    }
  }

  async function sendCode() {
    if (Date.now() < retryAt || !begin('Sending verification code…')) return;
    const trace = diagnostic('send');
    try {
      const client = getSupabaseBrowserClient();
      const { error } = await client.auth.signInWithOtp({
        email: targetEmail,
        options: { shouldCreateUser: true },
      });
      if (error) throw error;
      trace.finish('code_sent');
      if (!mounted.current) return;
      setSentEmail(targetEmail);
      setCode('');
      const sentAt = Date.now();
      setNow(sentAt);
      setRetryByEmail(previous => ({ ...previous, [targetEmail.toLowerCase()]: sentAt + 60_000 }));
      setMessage('Check your inbox for a six-digit verification code.');
    } catch (error) {
      trace.finish('failed', error);
      if (mounted.current) showError(error, 'Could not send the verification code. Please try again.');
    } finally {
      if (mounted.current) finish();
    }
  }

  async function verifyCode(event: React.FormEvent) {
    event.preventDefault();
    if (!sentEmail || !/^\d{6}$/.test(code) || !begin('Verifying code…')) return;
    const trace = diagnostic('verify');
    try {
      const { data, error } = await getSupabaseBrowserClient().auth.verifyOtp({
        email: sentEmail, token: code, type: 'email',
      });
      if (error) throw error;
      if (!data.session) throw Object.assign(new Error('Could not sign in. Please try again.'), { code: 'no_session' });
      trace.finish('verified');
      if (!mounted.current) return;
      // The existing workspace auth listener receives the verified session.
      setProgress('Opening your workspace…');
      setMessage('Signed in. Opening your workspace…');
    } catch (error) {
      trace.finish('failed', error);
      if (mounted.current) { showError(error, 'Could not sign in. Please try again.'); finish(); }
    }
  }

  return (
    <main className="radar-auth-page">
      <section className="radar-auth-story">
        <a href={RADAR_LOCALES.find(item => item.locale === locale)!.path} className="radar-auth-brand">
          <img src="/images/mindstream_logomark.png" alt="" />
          <span>Mindstream</span><i /> <strong>{t("Buyer Radar")}</strong>
        </a>
        <div>
          <span className="radar-workspace-eyebrow">{t("Private workspace")}</span>
          <h1>{t("Find the buyer.")}<br />{t("See the reason.")}<br />{t("Choose the next move.")}</h1>
          <p>{t("Your radar begins with a brief you can challenge—not a black-box list of leads.")}</p>
        </div>
        <footer><ShieldCheck size={17} />{' '}{t("No Gmail access required")}</footer>
      </section>

      <section className="radar-auth-card-wrap">
        <div className="radar-auth-card">
          <span className="radar-auth-index">{t("01 / ACCESS")}</span>
          <h2>{t(context === 'expo' ? 'Your Expo results, saved.' : 'Open your workspace')}</h2>
          {context === 'expo' && <p>{t('Use the email you shared at the Expo. Verify it to open your results and continue in Buyer Radar.')}</p>}
          {context !== 'expo' && <p>{t("Use any email address. Verify it to start your free trial.")}</p>}
          {!emailOnly && <><button className="radar-google-button" type="button" onClick={signInWithGoogle} disabled={busy}>
            <span className="radar-google-mark">G</span>{t(progress === 'Opening Google sign-in…' ? progress : 'Continue with Google')}{progress === 'Opening Google sign-in…' ? <LoaderCircle className="spin" size={17} /> : <ArrowRight size={17} />}
          </button>
          <div className="radar-auth-divider"><span>{t("or use your email")}</span></div></>}
          {!sentEmail ? <form onSubmit={(event) => { event.preventDefault(); void sendCode(); }}>
            <label htmlFor="radar-email">{t("Work email")}</label>
            <div className="radar-email-field">
              <Mail size={17} />
              <input
                id="radar-email"
                type="email"
                autoComplete="email"
                required
                disabled={busy}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <button type="submit" className="radar-primary-action" disabled={busy || secondsRemaining > 0}>
              {busy ? <><LoaderCircle className="spin" size={17} />{t(progress)}</> : <>{secondsRemaining > 0 ? t('Resend in {seconds}s', { seconds: secondsRemaining }) : t('Send verification code')}<ArrowRight size={17} /></>}
            </button>
          </form> : <form onSubmit={verifyCode}>
            <p className="radar-code-recipient">{t('Enter the code sent to {email}', { email: sentEmail })}</p>
            <label htmlFor="radar-code">{t('Verification code')}</label>
            <div className="radar-email-field"><Mail size={17} /><input id="radar-code" type="text"
              inputMode="numeric" autoComplete="one-time-code" autoFocus required pattern="[0-9]{6}" maxLength={6}
              disabled={busy} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" /></div>
            <button type="submit" className="radar-primary-action" disabled={busy || code.length !== 6}>
              {busy && progress !== 'Sending verification code…' ? <><LoaderCircle className="spin" size={17} />{t(progress)}</> : <>{t('Verify and sign in')}<ArrowRight size={17} /></>}</button>
            <button type="button" className="radar-secondary-action" disabled={busy || secondsRemaining > 0} onClick={() => void sendCode()}>
              {secondsRemaining > 0 ? t('Resend in {seconds}s', { seconds: secondsRemaining }) : t('Resend code')}</button>
            <button type="button" className="radar-secondary-action" disabled={busy} onClick={() => {
              setSentEmail(''); setCode(''); setMessage(''); setIsError(false);
            }}>{t('Use a different email')}</button>
          </form>}
          {message && <div className="radar-auth-message" role={isError ? 'alert' : 'status'}>{e(message)}</div>}
          {diagnosticId && <small className="radar-private">{t('Diagnostic ID: {id}', { id: diagnosticId })}</small>}
          <small>{t(context === 'expo' ? 'Your existing workspaces stay with you. Expo trial searches are added once per account.' : "Free credits are granted once. No payment details are required. Returning users keep their existing workspace.")}</small>
        </div>
      </section>
    </main>
  );
}
